// src/components/CalendarPage.js
import { useState, useEffect, useMemo } from "react";
import { useContacts } from "../context/ContactsContext";
import { useEvents } from "../context/EventsContext";
import { fetchContacts } from "../api/contacts";
import ContactInfo from "./ContactInfo";
import ContactSearchBar from "./ContactSearchBar";
import "./CalendarPage.css";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const DEFAULT_FILTERS = {
  hasEmail: "both",
  hasPhone: "both",
  customerTypes: [],
  contactMethods: [],
  contactOwners: [],
  tags: [],
  hasEvent: "both",
  canContact: "both",
};

const normalizeFilterArray = (value) => {
  const splitValue = (item) => {
    if (typeof item !== "string") return [item];
    return item
      .split(/[\s,]+/)
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const raw = Array.isArray(value) ? value.flatMap(splitValue) : splitValue(value);
  return raw
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
    .map((v) => (Number.isNaN(Number(v)) ? v : Number(v)));
};

const normalizeFilters = (filters = {}) => ({
  ...DEFAULT_FILTERS,
  ...filters,
  customerTypes: normalizeFilterArray(filters.customerTypes),
  contactMethods: normalizeFilterArray(filters.contactMethods),
  contactOwners: normalizeFilterArray(filters.contactOwners),
  tags: normalizeFilterArray(filters.tags),
});

const loadFiltersFromStorage = () => {
  try {
    const saved = localStorage.getItem("contactFilters");
    return saved ? normalizeFilters(JSON.parse(saved)) : DEFAULT_FILTERS;
  } catch {
    return DEFAULT_FILTERS;
  }
};

const hasActiveFilters = (filters = {}) => {
  return (
    (filters.hasEmail && filters.hasEmail !== "both") ||
    (filters.hasPhone && filters.hasPhone !== "both") ||
    (filters.hasEvent && filters.hasEvent !== "both") ||
    (filters.canContact && filters.canContact !== "both") ||
    (filters.customerTypes && filters.customerTypes.length > 0) ||
    (filters.contactMethods && filters.contactMethods.length > 0) ||
    (filters.contactOwners && filters.contactOwners.length > 0) ||
    (filters.tags && filters.tags.length > 0)
  );
};

function generateCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days = [];

  // Vormonat auffüllen
  const prevMonthLastDate = new Date(year, month, 0).getDate();
  for (let i = 0; i < startOffset; i++) {
    days.push(
      new Date(year, month - 1, prevMonthLastDate - (startOffset - 1 - i))
    );
  }

  // aktueller Monat
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(new Date(year, month, d));
  }

  // Folgemonat auffüllen bis 42 Felder (6 Wochen Grid)
  const totalCells = 42;
  const trailingCount = totalCells - days.length;
  for (let i = 1; i <= trailingCount; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

export default function CalendarPage() {
  const { selectedContactId, setSelectedContactId, contacts } = useContacts(); // ✅ contacts hinzugefügt
  const { events, loadEvents } = useEvents();
  const [setSearchTerm] = useState("");
  const [filters, setFilters] = useState(loadFiltersFromStorage);
  const [filteredContactIds, setFilteredContactIds] = useState(null);
  const [filteredContactMap, setFilteredContactMap] = useState(new Map());

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [showPastEvents, setShowPastEvents] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const calendarDays = generateCalendarDays(year, month);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  // Events laden wenn Monat ändert (inkl. Vormonat und Folgemonat für Kalenderanzeige)
  useEffect(() => {
    const from = new Date(year, month - 1, 1).toISOString().split("T")[0];
    const to = new Date(year, month + 2, 0).toISOString().split("T")[0];
    loadEvents(from, to);
  }, [year, month, loadEvents]);

  useEffect(() => {
    if (!hasActiveFilters(filters)) {
      setFilteredContactIds(null);
      setFilteredContactMap(new Map());
      return;
    }

    let active = true;

    (async () => {
      const all = [];
      let cursor = null;
      let guard = 0;

      do {
        const page = await fetchContacts({ limit: 200, cursor, filters });
        const items = Array.isArray(page.items) ? page.items : [];
        all.push(...items);
        cursor = page.nextCursor || null;
        guard += 1;
      } while (cursor && guard < 20);

      if (!active) return;

      const ids = new Set(all.map((c) => c.id));
      const map = new Map(all.map((c) => [c.id, c]));
      setFilteredContactIds(ids);
      setFilteredContactMap(map);
    })().catch(() => {
      if (!active) return;
      setFilteredContactIds(new Set());
      setFilteredContactMap(new Map());
    });

    return () => {
      active = false;
    };
  }, [filters]);

  const handleApplyFilters = (nextFilters) => {
    const normalized = normalizeFilters(nextFilters);
    setFilters(normalized);
    localStorage.setItem("contactFilters", JSON.stringify(normalized));
  };

  function formatLocalDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function eventsForDate(date) {
    const dateStr = formatLocalDate(date);
    return visibleEvents.filter((e) => e.day === dateStr);
  }

  const visibleEvents = useMemo(() => {
    if (!filteredContactIds) return events;
    return events.filter((e) => filteredContactIds.has(e.contactId));
  }, [events, filteredContactIds]);

  const contactLookup = useMemo(() => {
    if (filteredContactMap.size > 0) return filteredContactMap;
    return new Map((contacts || []).map((c) => [c.id, c]));
  }, [filteredContactMap, contacts]);

  // Events für das ausgewählte Datum
  const selectedDayEvents = eventsForDate(selectedCalendarDate);

  // Alle Events vor dem heutigen Datum
  const todayDate = new Date();
  const todayStr = formatLocalDate(todayDate);
  const pastEvents = visibleEvents.filter((e) => e.day < todayStr);

  const eventsToDisplay = showPastEvents 
    ? pastEvents
    : selectedDayEvents;

  return (
    <div className="calendar-page">
      <div
        className={`calendar-layout ${
          selectedContactId ? "with-contact" : "no-contact"
        }`}
      >
        {/* Linke Spalte: SearchBar + Kalender */}
        <div className="calendar-left-panel" style={{ position: "relative" }}>
          <ContactSearchBar 
            onSearch={setSearchTerm} 
            onSelectSuggestion={(contact) => setSelectedContactId(contact.id)}
            onFilter={handleApplyFilters}
            filters={filters}
          />

          <div className="calendar-column">
            <div className="calendar-section">
              {/* Monatsnavigation */}
              <div className="calendar-header">
                <button onClick={prevMonth}>←</button>
                <div className="calendar-title">
                  {currentDate.toLocaleString("de-DE", {
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <button onClick={nextMonth}>→</button>
              </div>

              {/* Wochentage */}
              <div className="calendar-grid weekdays">
                {WEEKDAYS.map((wd) => (
                  <div key={wd} className="weekday-header">
                    {wd}
                  </div>
                ))}
              </div>

              {/* Tage */}
              <div className="calendar-grid days">
                {calendarDays.map((date, idx) => {
                  const isToday =
                    date.toDateString() === new Date().toDateString();
                  const inCurrentMonth = date.getMonth() === month;
                  const isSelected = selectedCalendarDate?.toDateString() === date.toDateString();

                  return (
                    <div
                      key={idx}
                      className={`calendar-cell ${
                        !inCurrentMonth ? "faded" : ""
                      } ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedCalendarDate(date);
                        setShowPastEvents(false);
                      }}
                    >
                      <div className="date-header">{date.getDate()}</div>
                      <div className="event-list">
                        {eventsForDate(date).map((event) => {
                          const contact = contactLookup.get(event.contactId);
                          const cannotContact = contact?.canContact === false;
                          return (
                            <div
                              key={event.eventId}
                              className={`calendar-event ${cannotContact ? "cannot-contact" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedContactId(event.contactId);
                              }} // ✅ ID im Context speichern
                            >
                              {event.contactName}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Rechte Spalte: Sidebar oder ContactInfo */}
        {!selectedContactId ? (
          <div className="sidebar-section">
            <h3>{showPastEvents ? "Vergangene Events" : "Events vom " + selectedCalendarDate.toLocaleDateString("de-DE")}</h3>
            <button 
              className="toggle-past-events"
              onClick={() => setShowPastEvents(!showPastEvents)}
            >
              {showPastEvents ? "Hide Past Events" : "Show Past Events"}
            </button>
            <ul>
              {eventsToDisplay.length === 0 ? (
                <li className="no-events">Keine Events</li>
              ) : (
                eventsToDisplay.map((event) => (
                  <li 
                    key={event.eventId}
                    className="event-item"
                    onClick={() => setSelectedContactId(event.contactId)}
                  >
                    <span className="event-date">{event.day}</span>
                    <span className="event-contact">{event.contactName}</span>
                    {event.description && (
                      <span className="event-desc">{event.description}</span>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : (
          <div className="split-right-panel">
            <div className="calendar-sidebar">
              <h3>{showPastEvents ? "Vergangene Events" : "Events vom " + selectedCalendarDate.toLocaleDateString("de-DE")}</h3>
              <button 
                className="toggle-past-events"
                onClick={() => setShowPastEvents(!showPastEvents)}
              >
                {showPastEvents ? "Hide Past Events" : "Show Past Events"}
              </button>
              <ul>
                {eventsToDisplay.length === 0 ? (
                  <li className="no-events">Keine Events</li>
                ) : (
                  eventsToDisplay.map((event) => (
                    <li 
                      key={event.eventId}
                      className="event-item"
                      onClick={() => setSelectedContactId(event.contactId)}
                    >
                      <span className="event-date">{event.day}</span>
                      <span className="event-contact">{event.contactName}</span>
                      {event.description && (
                        <span className="event-desc">{event.description}</span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>
            <ContactInfo contactId={selectedContactId} />
          </div>
        )}
      </div>
    </div>
  );
}
